/**
 * PublicReach (26 Oct 2026, v2) — live audience numbers on the advertiser landing page.
 *  default export  PublicReach    pills under the heading: "👥 10+ app users · 🌍 N countries · 🐋 N species followed"
 *                                 (shown from 10 app users, rounded DOWN to 10)
 *  named export    SpeciesLookup  "How many listeners follow your species?" — pick a species → "15+ app users follow it"
 *                                 (rounded DOWN to 5; below 5: "fewer than 5 so far")
 * Both update automatically. One shared call to the public callable getPublicReachStats. Never shows an error.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase-config';
import { getSpeciesArray } from '../../shared/speciesUtils';

let shared = null;
function loadReach() {
  if (!shared) {
    shared = httpsCallable(functions, 'getPublicReachStats')({})
      .then((x) => x.data || null)
      .catch(() => { shared = null; return null; });
  }
  return shared;
}
function useReach() {
  const [r, setR] = useState(null);
  useEffect(() => {
    let alive = true;
    loadReach().then((d) => { if (alive) setR(d); });
    return () => { alive = false; };
  }, []);
  return r;
}

const pill = { background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff',
  borderRadius: 999, padding: '6px 14px', fontSize: '0.95rem' };

export default function PublicReach() {
  const r = useReach();
  if (!r || !r.users) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10, marginTop: 18 }}>
      <span style={pill}>👥 <b>{r.users}+</b> app users</span>
      {r.countries ? <span style={pill}>🌍 <b>{r.countries}</b> countries</span> : null}
      {r.species ? <span style={pill}>🐋 <b>{r.species}</b> species followed</span> : null}
    </div>
  );
}

const card = { maxWidth: 720, margin: '0 auto', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 14, padding: '20px 22px', textAlign: 'center', color: '#e8f1f8' };

export function SpeciesLookup() {
  const r = useReach();
  const [id, setId] = useState('');
  const all = useMemo(() => getSpeciesArray().sort((a, b) => a.name.localeCompare(b.name)), []);
  const counts = (r && r.speciesCounts) || {};
  const top = Object.entries(counts).map(([k, n]) => ({ id: Number(k), n }))
    .sort((a, b) => b.n - a.n).slice(0, 5)
    .map((t) => ({ ...t, name: (all.find((s) => s.id === t.id) || {}).name || `Species ${t.id}` }));
  const chosen = id ? all.find((s) => s.id === Number(id)) : null;
  const n = chosen ? counts[chosen.id] : null;

  return (
    <div className="benefits-section">
      <h2>How many listeners follow your species?</h2>
      <div style={card}>
        <select value={id} onChange={(e) => setId(e.target.value)}
          style={{ width: '100%', maxWidth: 420, padding: '10px 12px', borderRadius: 10, fontSize: 16, background: '#0f2537', color: '#e8f1f8', border: '1px solid #23405a' }}>
          <option value="">Choose a species…</option>
          {all.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {chosen && (
          <p style={{ fontSize: 18, marginTop: 16 }}>
            {n
              ? <>🐋 <b>{n}+</b> Cetalingua FM app users follow the <b>{chosen.name}</b>.</>
              : <>Fewer than 5 app users follow the <b>{chosen.name}</b> so far — the audience is growing.</>}
          </p>
        )}
        {top.length > 0 && (
          <p style={{ fontSize: 14, opacity: 0.75, marginTop: 14 }}>
            Most followed: {top.map((t) => `${t.name} ${t.n}+`).join(' · ')}
          </p>
        )}
        <p style={{ fontSize: 13, opacity: 0.6, marginTop: 10 }}>
          Live numbers, rounded down, updated automatically. Your content and push notifications reach users by species, wherever they live.
        </p>
      </div>
    </div>
  );
}
